/**
 * 보고서 생성 위저드
 * Composer 양식 선택 → 현장/점검 선택 → 양식 작성 페이지(/reports/new/composed/:key)로 진입
 *
 * 이 페이지 자체는 보고서를 만들지 않고, 손나열 정적 카드도 두지 않는다 — 1단계 카드는 서버가
 * category·entitlement로 필터해 내려준 enabled 템플릿뿐이다.
 *
 * ## 2단계는 서버 `reportGate`를 읽는다 — 이 화면은 게이트의 **독자**다 (2026-10-09)
 *
 * 점검 후보를 `getInspections`(= `findAll`)로 받으므로 응답에 점검별 `reportGate`가 실려 온다.
 * 그런데 종전엔 `status === 'COMPLETED'`만 걸러 **선택·서명 축을 전혀 보지 않았다** ⇒ 선택된
 * 개선대책이 **0건인 점검을 고를 수 있었고**, 집행은 「고른 점검 **전부**가 선택 ≥1」이라
 * (`reports.service.ts`의 `assertSelectedMeasuresPresent`) 작성 페이지까지 간 뒤 400으로 거절됐다.
 *
 * ⚠️ 이 화면은 「보고서 생성」 CTA **넷**의 공통 목적지다(대시보드 desktop·mobile QuickAction ·
 * `Layout` FAB · 문서관리 목록 CTA) — 그 넷에 각자 판정을 두지 않는 근거가 여기 있다.
 *
 * ⚠️ **권한 축을 점검별로 AND하지 않는 것이 의도다 — 행 메뉴와 다르다.** 정본은 결정
 * `docs/decisions/2026-10-09-the-enforcement-expression-is-the-canonical-gate.md`다: **집행 식이
 * 정본이고 화면은 소비만 한다**(결론 1·축 ③) — 서버가 `canCreateReport`를 집행 식으로 계산해
 * `reportGate`에 싣는다. 그 식은 **`reports.service.ts:1316`·`:1329`**이고 두 항의 **OR**이며
 * **신원 축이 앞에 있다**:
 *
 * ```
 * [그 점검의 활성 참가자 ∨ 주담당자]                     ← 신원 축 (OR, `.some`)
 *   ∨
 * [(소유자 ∨ 그 현장 배정) ∧ (소유자 ∨ report.create)]    ← scope ∧ 키
 * ```
 *
 * ⇒ 여기에 권한식을 또 두면 그 결정이 **없애려는 사본의 네 번째**가 된다. 손으로 AND하면 **두 방향
 * 으로** 집행보다 좁아진다: ⓐ 신원 축은 OR이라 **scope도 키도 없는 참가자**를 통과시키고 ⓑ 그 축은
 * **`.some`(고른 집합 중 하나라도)**이라 행마다 AND하는 것과 다른 연산이다 — **남의 점검을 함께
 * 묶는 것이 집행상 허용**이다. 그 결정의 prod 실측: 점검 목록의 손식 AND가 집행 통과 **145건 중
 * 46건을 거짓 차단**하고 있었다. 그리고 **scope 항은 이미 걸려 있다** — 아래 `loadSites`가
 * 비소유자에게 배정 현장만 남긴다(신원 축은 그보다 넓으니 여기서 좁힐 이유가 더 없다).
 *
 * ⚠️ **이 식과 수치의 정본은 `b2e9ef2c6` «이후»의 그 결정 기록이다 — 그 전 판은 신원 축이 빠져
 * 있었다**(144/45 + 「상세가 집행보다 1건 넓다」. 정정판에서 상세는 **양방향 0건**이다). 그 반쪽
 * 판을 인용한 주석이 한 번 이 자리에 들어왔다 — 결론이 같아 **아무것도 깨지지 않은 채 조용히
 * 전파된** 경우다. 수치를 인용하기 전에 그 문서의 「정정판」 열을 볼 것.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { useWebSocketStore } from '../../stores/websocket';
import {
  sitesApi,
  Site,
  isSiteSelectable,
  isSiteWritable,
} from '../../lib/organizations';
import {
  getInspections,
  InspectionListItem,
} from '../../lib/inspections';
import { ServerEvents, InspectionStatusChangedPayload } from '../../types/websocket';
import { ChevronLeftIcon } from '../../components/icons';
import { Badge, Button, Card, SelectableCard } from '@safetyhub/ui';
import { useEnabledComposerTemplates } from '../../hooks/useEnabledComposerTemplates';
import { apiErrorMessage } from '../../lib/api';
import { isSplitCapable, useSplitGroupEntry } from '../../components/reports/useSplitGroupEntry';
import { MODAL_LAYER } from '../../components/modalLayers';

/**
 * 위저드 단계 표식 — 2단계(현장·점검 선택)로 넘어갔음을 URL `?step=select`로 남긴다. 브라우저
 * 뒤로가기가 이 파라미터로 1단계/2단계를 되돌린다. (이 파라미터는 단계 표식이지 유형 표기가 아니다.)
 */
const STEP_PARAM = 'step';
const SELECT_STEP = 'select';

/** 시안 `.back-link` — 제목 위 뒤로가기(Link/button 양쪽이 같은 시각을 쓰도록 상수로). */
const BACK_LINK_CLASS =
  'inline-flex items-center gap-1.5 py-1.5 text-[13.5px] font-semibold text-ink-600 transition-colors hover:text-brand-600';

// 시안 `.select-card` 내부 선택 항목(현장/점검)의 톤은 여기서 상수로 들지 않는다 —
// 선택 어포던스는 `@safetyhub/ui`의 SelectableCard가 단일 소스로 소유한다.

/**
 * 이 점검을 고를 수 없나 — **서버 판정을 읽기만** 한다(식·문구의 정본은 shared `resolveReportGate`).
 *
 * ⚠️ 폴백이 `false`(= 고를 수 있다)인 것이 규약이다. 없는 필드로 잠그면 그 응답 경로의 기능이
 * 통째로 꺼진다(`lib/inspections.ts`의 `reportGate` JSDoc이 정본). 여기 식을 손으로 다시 쓰지
 * 말 것 — 래칫은 `lib/__tests__/reportGate.test.ts`(⑧-b)다.
 *
 * ⚠️ `lib/inspections.ts`에 공용 함수로 올리지 **않는다**: 이 화면의 spec이 그 모듈을
 * `vi.mock` 팩토리로 바꿔치기하므로(부분 mock이 아니다) 새 **런타임** export를 import하면 그
 * 모듈을 mock하는 spec 전부가 `No "…" export is defined on the mock`으로 죽는다. 타입
 * (`InspectionListItem`)은 컴파일에 지워지므로 안전하다.
 */
function isReportGateBlocked(inspection: Pick<InspectionListItem, 'reportGate'>): boolean {
  return inspection.reportGate?.canGenerate === false;
}

export default function NewReport() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, currentMembership } = useAuthStore();

  // 진입 지점 정보 (뒤로가기 링크용) — setSearchParams로 state가 사라지므로 ref로 보존
  const entryStateRef = useRef<{ from?: string; fromLabel?: string } | null>(null);
  if (entryStateRef.current === null && location.state) {
    const state = location.state as { from?: string; fromLabel?: string; entryFrom?: string; entryFromLabel?: string };
    // 하위 페이지(양식 작성 등)에서 돌아올 때 원래 진입 지점 복원
    if (state.entryFrom) {
      entryStateRef.current = { from: state.entryFrom, fromLabel: state.entryFromLabel };
    } else {
      entryStateRef.current = { from: state.from, fromLabel: state.fromLabel };
    }
  }
  const backLink = entryStateRef.current?.from || '/reports';
  const backLabel = entryStateRef.current?.fromLabel || '보고서 목록';
  const companyId = currentMembership?.companyId;

  // 선택 상태. `step`은 2단계 진입 표식(`select`)만 담는다.
  //
  // ⚠️ URL의 `?step=`으로 **초기화하지 않는다**. 2단계가 그리는 양식
  // (`composedTemplateKey`)은 React state에만 있고 URL엔 없으므로, 딥링크·새로고침으로 들어온
  // `?step=select`는 언제나 「단계 표식은 있는데 고른 양식이 없는」 상태다 — 종전엔 그대로 2단계를
  // 그려 카드는 「Composer 양식」 placeholder, 「보고서 작성」 버튼은 무반응이었다. 이 페이지는 점검
  // 단건을 직접 준비하지 않는다. 표식은 아래 동기화 effect가 키와 대조해 되살리거나 접는다.
  const [step, setStep] = useState<string | null>(null);
  const [siteId, setSiteId] = useState('');
  const [selectedInspectionIds, setSelectedInspectionIds] = useState<string[]>([]);

  // Composer 템플릿(동적 COMPOSED 카드) — Step1에 합류, 선택 시 Step2(단일 점검) 재사용.
  // 1단계 카드가 전부 동적이라 로드 중에 "종류 없음" 안내가 잠깐 스치지 않도록 로딩을 따로 든다 —
  // 훅이 그 축을 함께 준다. 서버가 category·entitlement로 이미 필터한 목록이다.
  const { templates: composerTemplates, isLoading: isComposerTemplatesLoading } =
    useEnabledComposerTemplates(companyId);
  const [composedTemplateKey, setComposedTemplateKey] = useState<string | null>(null);

  // 1단계에 그릴 카드가 하나도 없는 상태 — 로딩 표시와 "종류 없음" 안내를 가르는 기준.
  const hasNoStep1Cards = composerTemplates.length === 0;

  // searchParams와 step 동기화 (브라우저 뒤로가기 시 URL 변경 반영)
  useEffect(() => {
    const stepFromUrl = searchParams.get(STEP_PARAM);
    if (stepFromUrl && !composedTemplateKey) {
      // 표식은 있는데 고른 양식이 없다 — 딥링크·새로고침, 또는 「변경」으로 1단계에 돌아온 뒤의
      // 브라우저 앞으로가기. 2단계가 그릴 것이 없으므로 URL을 1단계로 접는다. `replace`인 이유:
      // 뒤로가기가 이 죽은 표식으로 다시 튕겨 오지 않게(교정 이동은 이력에 남기지 않는다).
      setSearchParams({}, { replace: true });
      return;
    }
    if (stepFromUrl !== step) {
      setStep(stepFromUrl);
      if (!stepFromUrl) {
        // 보고서 타입 선택 화면으로 돌아온 경우 상태 초기화
        setSiteId('');
        setSelectedInspectionIds([]);
      }
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // 검색 필터 상태
  const [siteSearchQuery, setSiteSearchQuery] = useState('');

  // 데이터 상태
  const [sites, setSites] = useState<Site[]>([]);
  const [inspections, setInspections] = useState<InspectionListItem[]>([]);

  // UI 상태
  const [isSitesLoading, setIsSitesLoading] = useState(true);
  const [isInspectionsLoading, setIsInspectionsLoading] = useState(false);
  const [error, setError] = useState('');

  /** Composer 양식 작성 페이지 진입(1문서) — 위저드는 고른 점검 **전체**를 컨텍스트로 넘긴다. */
  const navigateToComposedWrite = (templateKey: string) => {
    navigate(
      `/reports/new/composed/${encodeURIComponent(templateKey)}?inspectionIds=${selectedInspectionIds.join(',')}&siteId=${siteId}`,
    );
  };

  // 분할 생성(1점검 → N문서) 그룹 선택 — F-1(결정 2026-09-07): 위저드도 **점검 상세와 같은**
  // 그룹 선택을 경유한다(종전엔 그룹 선택 없이 직행해 단건이 만들어졌다). 절차는 점검 상세와
  // 공용 훅 하나를 쓰고, 여기 남는 것은 로딩·에러 표시와 이동 경로뿐이다.
  //
  // ⚠️ **위임 갈래는 위저드에 없다** — 위임 선택은 점검 상세의 문서 유형 선택 모달 전용이다
  // (결정 F-1). 이 화면은 본인 작성 진입로다.
  const [isPreparingSplit, setIsPreparingSplit] = useState(false);
  const splitEntry = useSplitGroupEntry({
    onCombined: (tpl) => navigateToComposedWrite(tpl.key),
    onCreated: (tpl, firstReportId) =>
      navigate(`/reports/new/composed/${encodeURIComponent(tpl.key)}?editReportId=${firstReportId}`),
    // 전건 skip — 새로 연 초안이 없으니 열 곳도 없다(안내 토스트는 훅이 띄웠다).
    onNoneCreated: () => navigate('/reports'),
    onCreateError: (message) => setError(message),
    // 층은 여는 쪽이 정한다 — 여기는 페이지 위 첫 모달이다.
    layer: MODAL_LAYER.root,
  });

  // WebSocket 구독
  const subscribe = useWebSocketStore((state) => state.subscribe);
  const isConnected = useWebSocketStore((state) => state.isConnected);

  // 보고서 종류가 선택되면 현장 목록 로드
  useEffect(() => {
    if (companyId && step) {
      loadSites();
    }
  }, [companyId, step]);

  // 현장 선택 시 점검 목록 로드
  useEffect(() => {
    if (companyId && siteId && step) {
      loadInspections();
    } else {
      setInspections([]);
    }
  }, [companyId, siteId, step]);

  // 점검 선택 시 자동 로드하지 않음 - "보고서 미리보기" 버튼 클릭 시 로드

  const loadSites = async () => {
    if (!companyId) return;

    setIsSitesLoading(true);
    try {
      const data = await sitesApi.findAll(companyId);
      // 일시중지·종료 현장은 선택지에서 제외(생애주기 축)
      let activeSites = data.filter(isSiteSelectable);
      // 비소유자는 할당된 현장에서만 보고서 생성 가능(소유자 성질 — P7-b-2, 옛 `role === 'SAFETY_MANAGER'`)
      if (currentMembership && !currentMembership.isOwner && user) {
        activeSites = activeSites.filter((site) =>
          site.assignments?.some((a) => a.member?.member?.userId === user.id),
        );
      }
      setSites(activeSites);
    } catch {
      setError('현장 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsSitesLoading(false);
    }
  };

  const loadInspections = async () => {
    if (!companyId || !siteId) return;

    setIsInspectionsLoading(true);
    try {
      // 점검별 기존 보고서 타입 매핑은 단일 선택 카드의 "보고서 생성됨" 배지 전용이었고,
      // 그 카드가 D7-S2에서 사라져 보고서 목록 조회도 함께 걷어냈다.
      const inspectionsResult = await getInspections(companyId, { siteId, limit: 50 });

      const inspectionsData = inspectionsResult.data || [];
      // 완료된 점검만 필터링. **막힌 점검은 목록에서 빼지 않는다** — 비활성으로 두고 사유를
      // 보여 주는 것이 「왜 못 고르나」를 알려 준다(빼면 그 점검이 사라져 보이고 사용자는
      // 목록이 잘못됐다고 읽는다).
      const completed = inspectionsData.filter((i) => i.status === 'COMPLETED');
      setInspections(completed);
      // 막힌 점검이 선택에 남아 있으면 덜어낸다 — 현장을 바꾸거나 WebSocket 갱신으로 목록이
      // 다시 올 때, 그 사이 선택했던 점검이 (발견사항 선택 해제 등으로) 막혔을 수 있다.
      const blockedIds = new Set(completed.filter(isReportGateBlocked).map((i) => i.id));
      if (blockedIds.size > 0) {
        setSelectedInspectionIds((prev) => prev.filter((id) => !blockedIds.has(id)));
      }
    } catch {
      setError('점검 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsInspectionsLoading(false);
    }
  };

  // 점검 상태 변경 핸들러
  // 단건 선택 상태 알림(구 안전점검 흐름 전용)은 D7-S2에서 제거 — 남은 두 흐름은 다중 선택이라
  // 목록 갱신으로 반영한다.
  const handleInspectionStatusChanged = useCallback(
    (data: InspectionStatusChangedPayload) => {
      // 점검 목록 갱신 조건:
      // 1. 목록에 있는 점검의 상태가 변경된 경우 (검토 해제 등)
      // 2. 같은 현장의 점검이 COMPLETED 상태로 변경된 경우 (검토 완료)
      const isInList = inspections.some(i => i.id === data.inspectionId);
      const isCompletedInSameSite = data.newStatus === 'COMPLETED' && data.siteId === siteId;

      if (siteId && (isInList || isCompletedInSameSite)) {
        loadInspections();
      }
    },
    [siteId, inspections, loadInspections]
  );

  // WebSocket 이벤트 구독 (현장이 선택되어 점검 목록을 보고 있는 경우)
  useEffect(() => {
    if (!isConnected || !siteId) return;

    const unsubscribe = subscribe<InspectionStatusChangedPayload>(
      ServerEvents.INSPECTION_STATUS_CHANGED,
      handleInspectionStatusChanged
    );

    return () => {
      unsubscribe();
    };
  }, [isConnected, siteId, subscribe, handleInspectionStatusChanged]);

  if (!companyId) {
    return (
      <div className="text-center py-12">
        <p className="text-sm font-medium text-ink-500">조직을 선택해주세요.</p>
      </div>
    );
  }

  // Composer 템플릿 선택 → Step2(현장→점검 다중 선택)
  const handleComposerTemplateSelect = (templateKey: string) => {
    setComposedTemplateKey(templateKey);
    setStep(SELECT_STEP);
    setSearchParams({ [STEP_PARAM]: SELECT_STEP });
  };

  // 뒤로가기 (보고서 종류 선택으로)
  const handleBackToTypeSelect = () => {
    setStep(null);
    setSiteId('');
    setSelectedInspectionIds([]);
    setComposedTemplateKey(null);
    setSearchParams({});
  };

  // 현재 단계 계산 (1: 종류 선택 / 2: 현장·점검 선택 — 작성은 각 전용 페이지가 담당)
  const currentStep = !step ? 1 : 2;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* 헤더 */}
      <div className="mb-6">
          {currentStep === 1 ? (
            <Link to={backLink} className={BACK_LINK_CLASS}>
              <ChevronLeftIcon className="h-4 w-4" />
              {backLabel}
            </Link>
          ) : (
            <button onClick={handleBackToTypeSelect} className={BACK_LINK_CLASS}>
              <ChevronLeftIcon className="h-4 w-4" />
              새 보고서 선택
            </button>
          )}
          <h1 className="mt-1 text-[26px] font-extrabold tracking-tight text-ink-900">
            {currentStep === 1 ? '새 보고서 선택' : '새 보고서 생성'}
          </h1>
          <p className="mt-1 text-[15px] font-medium text-ink-500">
            {currentStep === 1
              ? '생성할 보고서 종류를 선택해주세요.'
              : '현장과 점검을 선택해주세요.'}
          </p>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="mb-6 rounded-card bg-red-50 p-4">
          <div className="flex">
            <ExclamationIcon className="h-5 w-5 text-red-400" />
            <p className="ml-3 text-sm text-red-700">{error}</p>
            <button
              onClick={() => setError('')}
              className="ml-auto text-red-400 hover:text-red-500"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Step 1: 보고서 종류 선택 */}
      {!step && (
        <div className="space-y-4">
          {/* 템플릿 목록이 오기 전에는 "종류 없음" 안내를 그리지 않는다(빈 목록이 한 번 스쳤다 뒤늦게
              카드가 붙는 깜빡임 방지). 종전의 피처 플래그 로딩 축은 플래그를 코드에 녹이며(라운드 4) 사라졌다. */}
          {hasNoStep1Cards && isComposerTemplatesLoading ? (
            <div className="flex items-center justify-center py-8 text-sm font-medium text-ink-500">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-600 mr-2"></div>
              불러오는 중...
            </div>
          ) : hasNoStep1Cards ? (
            <div className="rounded-card border border-dashed border-line bg-surface-soft py-8 text-center text-[13px] font-medium text-ink-400">
              <p>이 화면에서 생성할 수 있는 보고서 종류가 없습니다.</p>
            </div>
          ) : (
          <div className="grid gap-[18px] sm:grid-cols-2 lg:grid-cols-3">
            {/* Composer enabled 템플릿 동적 카드 (점검 앵커 COMPOSED) — 1단계 카드는 이것뿐이다 */}
            {composerTemplates.map((tpl) => (
              <button
                key={tpl.key}
                type="button"
                onClick={() => handleComposerTemplateSelect(tpl.key)}
                className="relative flex cursor-pointer flex-col items-center rounded-card-lg border border-line bg-white p-6 text-left shadow-card-sm transition-colors hover:border-brand-300 hover:bg-brand-50/60"
              >
                <div className="mb-3 grid h-12 w-12 place-items-center rounded-card bg-brand-50 text-brand-600">
                  <ReportIcon className="w-6 h-6" />
                </div>
                <h3 className="text-center text-[15px] font-bold tracking-tight text-ink-900">{tpl.name}</h3>
                <p className="mt-1 text-center text-[13px] font-medium leading-relaxed text-ink-500">
                  Composer 양식 · 점검 결과를 기반으로 생성합니다.
                </p>
              </button>
            ))}
          </div>
          )}
        </div>
      )}

      {/* Step 2: 현장/점검 선택 (직접 선택하는 경우만) */}
      {step && (
        <div className="space-y-6">
          {/* 선택된 보고서 종류 표시 + 뒤로가기 */}
          {/* 시안 `.site-info-card` — 흰 면 + line 테두리로, 강조는 아이콘 타일에만 준다. */}
          <Card radius="lg" padding="none" elevation="md" className="flex items-center justify-between gap-4 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 flex-none place-items-center rounded-card bg-brand-50 text-brand-600">
                <ReportIcon className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-ink-500">선택된 보고서 종류</p>
                <p className="mt-0.5 text-[17px] font-extrabold tracking-tight text-ink-900">
                  {composerTemplates.find((t) => t.key === composedTemplateKey)?.name ?? 'Composer 양식'}
                </p>
              </div>
            </div>
            <Button variant="secondary" size="sm" onClick={handleBackToTypeSelect} className="flex-none">
              변경
            </Button>
          </Card>

          {/* 현장/점검 선택 패널 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-[18px]">
            {/* 현장 선택 */}
            <Card radius="lg" padding="lg" elevation="md" className="flex flex-col gap-3.5">
              <h3 className="text-[14.5px] font-bold tracking-tight text-ink-900">
                현장 선택 <span className="text-rose-600">*</span>
              </h3>

              {/* 현장 검색 */}
              <div className="relative">
                <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400 pointer-events-none" />
                <input
                  type="text"
                  value={siteSearchQuery}
                  onChange={(e) => setSiteSearchQuery(e.target.value)}
                  placeholder="현장 이름으로 검색..."
                  className="w-full rounded-control border border-line bg-surface-soft py-2.5 pl-10 pr-8 text-[13.5px] text-ink-900 transition-colors placeholder:text-ink-400 focus:border-brand-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/15"
                />
                {siteSearchQuery && (
                  <button
                    type="button"
                    onClick={() => setSiteSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-400 transition-colors hover:text-ink-600"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* 현장 목록 */}
              <div className="max-h-64 overflow-y-auto">
                {isSitesLoading ? (
                  <div className="flex items-center justify-center py-8 text-sm font-medium text-ink-500">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-600 mr-2"></div>
                    불러오는 중...
                  </div>
                ) : sites.length === 0 ? (
                  <div className="rounded-card border border-dashed border-line bg-surface-soft py-8 text-center text-[13px] font-medium text-ink-400">
                    <p>등록된 현장이 없습니다.</p>
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {sites
                      .filter((site) =>
                        site.name.toLowerCase().includes(siteSearchQuery.toLowerCase()) ||
                        (site.address && site.address.toLowerCase().includes(siteSearchQuery.toLowerCase()))
                      )
                      .map((site) => {
                        // 이관중 현장은 목록에 남기되 고를 수 없다 — 이관 플로우가 소유 중이라
                        // 골라도 서버가 거절한다(`CreateInspectionModal`과 같은 결).
                        const isPending = !isSiteWritable(site);
                        return (
                          <li key={site.id}>
                            <SelectableCard
                              selected={siteId === site.id}
                              disabled={isPending}
                              onClick={() => {
                                setSiteId(site.id);
                                setSelectedInspectionIds([]);
                              }}
                              icon={<BuildingIcon className="w-[18px] h-[18px]" />}
                              title={
                                isPending ? (
                                  <span className="flex items-center gap-1.5">
                                    {site.name}
                                    <Badge tone="amber" shape="rounded">
                                      이관대기
                                    </Badge>
                                  </span>
                                ) : (
                                  site.name
                                )
                              }
                              subtitle={site.address || undefined}
                            />
                          </li>
                        );
                      })}
                    {sites.filter((site) =>
                      site.name.toLowerCase().includes(siteSearchQuery.toLowerCase()) ||
                      (site.address && site.address.toLowerCase().includes(siteSearchQuery.toLowerCase()))
                    ).length === 0 && (
                      <li className="rounded-card border border-dashed border-line bg-surface-soft py-8 text-center text-[13px] font-medium text-ink-400">
                        <p>검색 결과가 없습니다.</p>
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </Card>

            {/* 점검 선택 */}
            <Card radius="lg" padding="lg" elevation="md" className="flex flex-col gap-3.5">
              <h3 className="text-[14.5px] font-bold tracking-tight text-ink-900">
                점검 선택 <span className="text-rose-600">*</span>
              </h3>

              {/* 점검 목록 */}
              <div className="max-h-80 overflow-y-auto">
                {!siteId ? (
                  <div className="flex flex-col items-center gap-1.5 rounded-card border border-dashed border-line bg-surface-soft py-12 text-center text-[13px] font-medium text-ink-400">
                    <BuildingIcon className="w-6 h-6 text-ink-300" />
                    <p>먼저 현장을 선택해주세요.</p>
                  </div>
                ) : isInspectionsLoading ? (
                  <div className="flex items-center justify-center py-8 text-sm font-medium text-ink-500">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-600 mr-2"></div>
                    불러오는 중...
                  </div>
                ) : inspections.length === 0 ? (
                  <div className="rounded-card border border-dashed border-line bg-surface-soft py-8 text-center text-[13px] font-medium text-ink-400">
                    <p className="mb-2">완료된 점검이 없습니다.</p>
                    <Link
                      to="/inspections/new"
                      className="text-[13px] font-semibold text-brand-600 hover:text-brand-700"
                    >
                      점검 시작하기
                    </Link>
                  </div>
                ) : (
                  // 남은 두 흐름(위험성평가·Composer 양식)이 모두 다중 점검 선택이라 단일 선택 분기는
                  // D7-S2에서 제거했다 — 구 안전점검이 유일한 단일 선택 사용처였다.
                  <ul className="space-y-2.5">
                    {[...inspections]
                      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                      .map((item, idx) => {
                        const checked = selectedInspectionIds.includes(item.id);
                        const round = idx + 1;
                        // 서버 판정 — 막힌 점검은 고를 수 없고 **왜인지 카드 아래에서 말한다**.
                        const blocked = isReportGateBlocked(item);
                        const reason = item.reportGate?.disabledReason;
                        const reasonId = `new-report-inspection-reason-${item.id}`;
                        return (
                          <li key={item.id}>
                            <SelectableCard
                              selected={checked}
                              disabled={blocked}
                              aria-describedby={blocked && reason ? reasonId : undefined}
                              onClick={() => {
                                setSelectedInspectionIds(prev =>
                                  prev.includes(item.id)
                                    ? prev.filter(id => id !== item.id)
                                    : [...prev, item.id]
                                );
                              }}
                              title={
                                <span className="tabular-nums">
                                  {round}차 · {new Date(item.createdAt).toLocaleDateString('ko-KR')}
                                </span>
                              }
                              subtitle={
                                <>
                                  {item.status === 'COMPLETED' ? '완료' : '검토대기'}
                                  {item.hazardCount ? ` · 발견사항 ${item.hazardCount}건` : ''}
                                  {item.photoCount ? ` · 사진 ${item.photoCount}건` : ''}
                                </>
                              }
                            />
                            {/* 사유를 카드 **밖**에 두는 이유: `SelectableCard`의 `subtitle`은
                                `truncate`라 「무엇을 해야 하나」가 잘리고, 잘림은 jsdom에 레이아웃
                                엔진이 없어 어느 spec도 못 잡는다. */}
                            {blocked && reason && (
                              <p
                                id={reasonId}
                                className="mt-1 px-3.5 text-xs font-medium text-amber-700"
                              >
                                {reason}
                              </p>
                            )}
                          </li>
                        );
                      })}
                  </ul>
                )}
              </div>
            </Card>
          </div>

          {/* 보고서 미리보기/작성 버튼 (시안 `.page-actions` — 가운데 정렬 + min-w 180px) */}
          <div className="flex justify-center gap-3 pt-4">
            <Button
              variant="primary"
              className="min-w-[200px] font-bold"
              onClick={async () => {
                // Composer 템플릿(COMPOSED): 다중 점검 컨텍스트로 작성 페이지 진입
                if (!composedTemplateKey || selectedInspectionIds.length === 0) return;
                // 분할을 선언한 양식이면 **그룹 선택을 경유**한다(F-1) — 안 하면 나눠야 할
                // 양식이 여기서만 단건으로 만들어져 진입점마다 결과가 갈린다.
                //
                // 고른 점검을 **전부** 넘긴다. 서버는 다점검을 받고(계획·생성 DTO 모두
                // `inspectionIds` 배열, 행 resolve도 전 점검을 훑는다) 분류만 앵커 점검
                // 기준이라(`tbm-work-type.split-provider`) 나머지 점검의 행은 미분류 그룹으로
                // 모인다 — 모달이 그 그룹을 보여 주고 사용자가 켤 수 있다. 여기서 점검 1개
                // 제한을 새로 두면 서버에 없는 조건을 화면이 신설하는 셈이 된다.
                const template = composerTemplates.find((t) => t.key === composedTemplateKey);
                if (template && isSplitCapable(template) && companyId && siteId) {
                  setIsPreparingSplit(true);
                  setError('');
                  try {
                    const outcome = await splitEntry.begin(template, {
                      companyId,
                      siteId,
                      inspectionIds: selectedInspectionIds,
                    });
                    setIsPreparingSplit(false);
                    if (outcome === 'opened') return;
                  } catch (err) {
                    // 조용히 종전 흐름으로 흘려보내면 사용자가 고를 기회 없이 1건이 만들어진다.
                    setIsPreparingSplit(false);
                    // ⚠️ `extractApiError(...).message`를 쓰지 말 것 — 그 헬퍼는 `NETWORK_ERROR`·
                    // `UNKNOWN_ERROR`에도 문구를 **늘 채우므로**(`lib/api.ts:378`) `||` 폴백이 영영
                    // 발동하지 않고 네트워크 단절에 axios 날문구가 뜬다. 정본은 `apiErrorMessage`(`:409`).
                    setError(apiErrorMessage(err) ?? '생성 범위를 확인하지 못했습니다.');
                    return;
                  }
                }
                navigateToComposedWrite(composedTemplateKey);
              }}
              disabled={!siteId || selectedInspectionIds.length === 0 || isPreparingSplit}
            >
              <ReportIcon className="w-4 h-4" />
              {isPreparingSplit
                ? '생성 범위를 확인하는 중...'
                : `보고서 작성 (${selectedInspectionIds.length}개 점검)`}
            </Button>
          </div>

          {/* 선택 상태 안내 */}
          {(!siteId || selectedInspectionIds.length === 0) && (
            <p className="text-center text-[13px] font-medium text-ink-500">
              {!siteId
                ? '현장을 선택해주세요.'
                : '점검을 선택해주세요.'}
            </p>
          )}
        </div>
      )}

      {/* 분할 그룹 선택(F-1) — 점검 상세와 같은 모달·같은 절차. */}
      {splitEntry.modal}
    </div>
  );
}

// Icons
function ExclamationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function ReportIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  );
}


